import { Component, inject, OnInit } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FormBuilder, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { EnhancedFormGroup } from '../data-table/app-model-modal/enhanced-form-group';
import { User, UserRole } from '../models/user';
import { AuthService } from '../services/auth.service';
import { AuthorizationService } from '../services/authorization.service';
import { RequestVerificationService } from '../services/user/request-verification';
import { RequestVerificationCodeType } from '../models/requestVerificationCodeType';
import { UserService } from '../services/user/user.service';
import { KeycloakCredentialMetadata } from '../models/keycloakCredential';
import { OrganizationService } from '../services/organization.service';
import { IMPERSONATION_SESSION_STORAGE_KEY } from '../utils/constants.impersonation';
import { HeaderBarComponent } from '../components/header-bar/header-bar.component';
import { ErrorComponent } from '../components/error/error.component';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { InputWithErrorComponent } from '../data-table/app-model-modal/input-with-error.component';
import { LaddaModule } from 'angular2-ladda';
import { DateTimePipe } from '../pipes/date-time.pipe';
import { UserRolesPipe } from '../pipes/user-roles.pipe';

_('profile.title');

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    imports: [HeaderBarComponent, ErrorComponent, TranslatePipe, TranslatePipe, NgClass, FormsModule, ReactiveFormsModule, InputWithErrorComponent, LaddaModule, DateTimePipe, UserRolesPipe],
})
export class ProfileComponent implements OnInit {
  form!: EnhancedFormGroup<{ [key: string]: FormControl<string | null | undefined> }>;

  user!: User;

  edit = false;

  error?: HttpErrorResponse;

  loading = false;

  verificationCodeRequested = false;

  verificationCodeRequestLoading = false;

  private previousEmail?: string;

  isImpersonating: boolean;

  otpCredentials?: KeycloakCredentialMetadata[];

  passkeyCredentials?: KeycloakCredentialMetadata[];

  apiKey?: string;

  maskedApiKey?: string;

  showApiKey = false;

  apiKeyCopied?: { success?: true; error?: true } = undefined;

  private authService = inject(AuthService);
  private authorizationService = inject(AuthorizationService);
  private userService = inject(UserService);
  private organizationService = inject(OrganizationService);
  private requestVerificationService = inject(RequestVerificationService);
  private fb = inject(FormBuilder);

  constructor() {
    this.isImpersonating = sessionStorage.getItem(IMPERSONATION_SESSION_STORAGE_KEY) !== null;
  }

  ngOnInit(): void {
    this.authService.getCurrentUser().subscribe((user) => {
      this.user = user;
      this.previousEmail = this.user.email;
      this.setFormGroup(this.user);
      if (this.canViewOrganizationApiKey()) {
        this.organizationService.getApiKey().subscribe((apiKey) => {
          this.apiKey = apiKey;
          this.maskedApiKey = apiKey.replace(/^(.{4}).+(.{4})$/, '$1****-****-****-****-********$2');
        });
      }
    });
    this.authService.getCredentials().subscribe((credentials) => {
      this.otpCredentials = credentials.filter((c) => c.type === 'otp').flatMap((c) => c.userCredentialMetadatas);
      this.passkeyCredentials = credentials.filter((c) => c.type === 'webauthn-passwordless').flatMap((c) => c.userCredentialMetadatas);
    });
    // Check if we have kc_action_status in the session storage
    const kcActionStatus = sessionStorage.getItem('kc_action_status');
    if (kcActionStatus !== null && kcActionStatus !== 'success') {
      // Remove the kc_action_status from the session storage
      sessionStorage.removeItem('kc_action_status');

      // Display the error message
      this.error = new HttpErrorResponse({
        status: 400,
        statusText: 'Bad Request',
        error: {
          error: `error.custom.kc_action_status.${kcActionStatus}`,
          error_description: kcActionStatus,
        },
      });
    }
  }

  protected setFormGroup(user: User): void {
    this.form = this.fb.group({
      id: user.userId,
      email: [user.email, [Validators.email]],
      given_name: [user.given_name, [Validators.required, Validators.minLength(2), Validators.maxLength(30)]],
      family_name: [user.family_name, [Validators.required, Validators.minLength(2), Validators.maxLength(30)]],
      role: [user.role, [Validators.required]],
      verificationCode: [user.verificationCode, []],
      newPassword: [user.newPassword, []],
    });
  }

  editProfile() {
    this.edit = true;
  }

  cancelEdit() {
    this.edit = false;
    this.setFormGroup(this.user);
  }

  saveProfile() {
    if (this.form.valid) {
      this.error = undefined;
      this.loading = true;
      this.user = Object.assign(this.user, this.form.value);
      this.userService.updateProfile(this.user).subscribe({
        next: async (user) => {
          this.edit = false;
          this.loading = false;
          this.user = user;

          // If the email was changed, the user needs to login again (and verify the new email)
          if (this.previousEmail !== this.user.email) {
            await this.authService.logout();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.loading = false;
          this.error = error;
        },
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  add2FADevice() {
    this.authService.add2FADevice();
  }

  addPasskey() {
    this.authService.registerWebauthnPasswordless();
  }

  removeKeycloakCredential(id: string) {
    this.authService.removeKeycloakCredential(id);
  }

  requestVerificationCode() {
    this.error = undefined;
    this.verificationCodeRequestLoading = true;

    // Update validators for email and password
    this.form.controls['newPassword'].setValidators([Validators.minLength(8)].concat(AuthService.pwdValidators));
    this.form.controls['verificationCode'].setValidators([Validators.required]);
    this.form.controls['email'].setValidators([Validators.required, Validators.email]);

    // Request verification code
    this.requestVerificationService
      .initiateRequestVerification(RequestVerificationCodeType.PASSWORD_OR_EMAIL_CHANGE)
      .subscribe({
        next: () => {
          this.verificationCodeRequestLoading = false;
          this.verificationCodeRequested = true;
        },
        error: (error: HttpErrorResponse) => {
          this.verificationCodeRequestLoading = false;
          this.error = error;
        },
      });
  }

  get apiKeyCopyIcon(): string {
    if (this.apiKeyCopied?.success) return 'check';
    if (this.apiKeyCopied?.error) return 'close';
    return 'content_copy';
  }

  copyApiKey() {
    if (!this.apiKey) {
      return;
    }

    // Copies the API key to the clipboard
    navigator.clipboard.writeText(this.apiKey!).then(() => {
      this.apiKeyCopied = { success: true };
      setTimeout(() => {
        this.apiKeyCopied = undefined;
      }, 2000);
    }).catch(() => {
      this.apiKeyCopied = { error: true };
      setTimeout(() => {
        this.apiKeyCopied = undefined;
      }, 2000);
    });
  }

  canViewOrganizationApiKey(): boolean {
    return this.authorizationService.hasRole(UserRole.ADMIN, this.user)
      || this.authorizationService.hasRole(UserRole.MANAGER, this.user);
  }
}
